import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgbDropdown, NgbDropdownModule, PlacementArray } from '@ng-bootstrap/ng-bootstrap';
import {
  DocPopupMenuFlatEntry,
  DocPopupMenuItem,
  flattenDocPopupMenuItems,
} from './doc-popup-menu.types';

@Component({
  selector: 'doc-popup-menu',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule],
  template: `
    <div
      ngbDropdown
      #dropdown="ngbDropdown"
      container="body"
      [placement]="placement"
      [autoClose]="true"
      (openChange)="onOpenChange($event)"
      class="doc-popup-menu-dropdown"
      appendTo="body">
      <button
        #anchorRef
        type="button"
        class="doc-popup-menu-anchor"
        ngbDropdownToggle
        tabindex="-1"
        aria-hidden="true"
        [style.left.px]="anchorLeft"
        [style.top.px]="anchorTop"
        [style.width.px]="anchorWidth"
        [style.height.px]="anchorHeight"></button>

      <div
        ngbDropdownMenu
        class="doc-popup-menu-panel dropdown-menu"
        [ngClass]="styleClass"
        [ngStyle]="style">
        @for (entry of flatModel; track entry.id) {
          @if (entry.kind === 'separator') {
            <div class="dropdown-divider"></div>
          } @else if (entry.kind === 'header') {
            <div class="dropdown-header doc-popup-menu-header">
              <div class="doc-popup-menu-header-title">{{ entry.label }}</div>
              @if (entry.subtitle) {
                <div class="doc-popup-menu-header-subtitle">{{ entry.subtitle }}</div>
              }
            </div>
          } @else {
            <button
              type="button"
              class="dropdown-item doc-popup-menu-item"
              [class.disabled]="entry.item.disabled"
              [ngClass]="entry.item.styleClass"
              [disabled]="entry.item.disabled"
              (click)="onItemClick(entry.item, $event)">
              @if (entry.item.icon) {
                <i [class]="entry.item.icon + ' doc-popup-menu-icon'" aria-hidden="true"></i>
              }
              @if (entry.item.escape === false) {
                <span class="doc-popup-menu-text" [innerHTML]="entry.item.label"></span>
              } @else {
                <span class="doc-popup-menu-text">{{ entry.item.label }}</span>
              }
            </button>
          }
        }
      </div>
    </div>
  `,
  styleUrl: './doc-popup-menu.component.scss',
})
export class DocPopupMenuComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() model: DocPopupMenuItem[] | null = [];
  @Input() styleClass = '';
  @Input() style: Record<string, string> | null = null;
  /** Kept for PrimeNG API compatibility. Popup mode is always used. */
  @Input() popup: boolean | string = true;
  /** Kept for PrimeNG API compatibility. Body container is always used. */
  @Input() appendTo?: string;

  @Output() onHide = new EventEmitter<void>();

  @ViewChild('dropdown') private dropdown!: NgbDropdown;
  @ViewChild('anchorRef') private anchorRef?: ElementRef<HTMLElement>;

  @HostBinding('class.doc-popup-menu-host')
  readonly hostClass = true;

  anchorLeft = 0;
  anchorTop = 0;
  anchorWidth = 1;
  anchorHeight = 1;
  placement: PlacementArray = ['bottom-end', 'top-end', 'bottom-start', 'top-start'];

  get flatModel(): DocPopupMenuFlatEntry[] {
    return flattenDocPopupMenuItems(this.model);
  }

  get visible(): boolean {
    return this.dropdown?.isOpen() ?? false;
  }

  toggle(target: HTMLElement | Event): void {
    this.positionAt(target);
    this.dropdown.toggle();
    // Re-check after open so Popper/ng-bootstrap can flip using real menu height.
    queueMicrotask(() => this.ensureInViewport());
  }

  show(target: HTMLElement | Event): void {
    this.positionAt(target);
    if (!this.dropdown.isOpen()) {
      this.dropdown.open();
    }
    queueMicrotask(() => this.ensureInViewport());
  }

  hide(): void {
    this.dropdown.close();
  }

  onOpenChange(open: boolean): void {
    if (!open) {
      this.onHide.emit();
      return;
    }
    queueMicrotask(() => this.ensureInViewport());
  }

  onItemClick(item: DocPopupMenuItem, event: Event): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    item.command?.({ originalEvent: event, item });
    this.hide();
  }

  private resolveTarget(targetInput: HTMLElement | Event): HTMLElement | null {
    let target: HTMLElement | null = null;

    if (targetInput instanceof HTMLElement) {
      target = targetInput;
    } else if (targetInput && typeof targetInput === 'object') {
      const event = targetInput as Event;
      target = (event.currentTarget ?? event.target) as HTMLElement | null;
    }

    // Prefer the clicked control (button), not an inner icon/label.
    while (target && target !== document.body) {
      if (target.tagName?.toLowerCase() === 'button') {
        return target;
      }
      target = target.parentElement;
    }

    return null;
  }

  private estimateMenuHeight(): number {
    const entries = this.flatModel;
    const itemCount = entries.filter((entry) => entry.kind === 'item').length;
    const headerCount = entries.filter((entry) => entry.kind === 'header').length;
    const separatorCount = entries.filter((entry) => entry.kind === 'separator').length;
    const headerWithSubtitleCount = entries.filter(
      (entry) => entry.kind === 'header' && !!entry.subtitle
    ).length;
    // Approximate rendered sizes from component styles.
    return Math.max(
      120,
      itemCount * 40 +
        headerCount * 28 +
        headerWithSubtitleCount * 16 +
        separatorCount * 12 +
        16
    );
  }

  private positionAt(targetInput: HTMLElement | Event): void {
    const target = this.resolveTarget(targetInput);
    let left = 0;
    let top = 0;
    let width = 1;
    let height = 1;

    if (target?.getBoundingClientRect) {
      const rect = target.getBoundingClientRect();
      left = rect.left;
      top = rect.top;
      width = Math.max(rect.width, 1);
      height = Math.max(rect.height, 1);

      const menuHeight = this.estimateMenuHeight();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      this.placement = openUpward
        ? ['top-end', 'top-start', 'bottom-end', 'bottom-start']
        : ['bottom-end', 'bottom-start', 'top-end', 'top-start'];
    } else if (targetInput instanceof MouseEvent) {
      left = targetInput.clientX;
      top = targetInput.clientY;
      width = 1;
      height = 1;
      this.placement = ['bottom-end', 'top-end', 'bottom-start', 'top-start'];
    }

    this.anchorLeft = left;
    this.anchorTop = top;
    this.anchorWidth = width;
    this.anchorHeight = height;

    if (this.anchorRef?.nativeElement) {
      const el = this.anchorRef.nativeElement;
      el.style.position = 'fixed';
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
    }

    this.cdr.detectChanges();
  }

  private ensureInViewport(): void {
    if (!this.dropdown?.isOpen()) {
      return;
    }

    const menuEl = document.querySelector(
      'body > .dropdown-menu.doc-popup-menu-panel.show, body > .doc-popup-menu-panel.dropdown-menu.show'
    ) as HTMLElement | null;

    if (!menuEl) {
      return;
    }

    const rect = menuEl.getBoundingClientRect();
    const padding = 8;
    let shiftX = 0;
    let shiftY = 0;

    if (rect.bottom > window.innerHeight - padding) {
      shiftY = window.innerHeight - padding - rect.bottom;
    } else if (rect.top < padding) {
      shiftY = padding - rect.top;
    }

    if (rect.right > window.innerWidth - padding) {
      shiftX = window.innerWidth - padding - rect.right;
    } else if (rect.left < padding) {
      shiftX = padding - rect.left;
    }

    if (!shiftX && !shiftY) {
      return;
    }

    const currentTransform = menuEl.style.transform || '';
    const match = /translate3d\(\s*([-.\d]+)px,\s*([-.\d]+)px/.exec(currentTransform);
    const baseX = match ? Number(match[1]) : 0;
    const baseY = match ? Number(match[2]) : 0;
    menuEl.style.transform = `translate3d(${baseX + shiftX}px, ${baseY + shiftY}px, 0)`;
  }
}
